<template>
  <main class="account-access">
    <section class="account-access__intro">
      <div class="account-access__copy">
        <div class="account-access__eyebrow">
          <img :src="grooveLogo" alt="Groove" />
          <span>Account access update</span>
        </div>
        <h1>Keep your Groove account and everything you built.</h1>
        <p class="account-access__lead">
          Groove.cm now supports paid accounts. Upgrade to Pro before the deadline to restore
          your dashboard access and keep your account data available.
        </p>

        <div class="account-access__actions">
          <a
            class="account-access__button account-access__button--primary"
            :href="pricingUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Pro plans
            <span aria-hidden="true">&#8599;</span>
          </a>
          <a class="account-access__button account-access__button--affiliate" :href="affiliateUrl">
            Continue as an affiliate
          </a>
        </div>

        <p class="account-access__reassurance">
          Your published sites, domains, checkout links, stores, courses, videos, webinars,
          email automations, rebills, and customer experiences continue to operate during the
          upgrade period.
        </p>
      </div>

      <aside class="account-access__plan" aria-label="Groove and Scale Plus Pro pricing">
        <span class="account-access__plan-label">Every app</span>
        <h2>Pro</h2>
        <div class="account-access__prices">
          <div>
            <strong>$95</strong>
            <span>/month</span>
          </div>
          <div>
            <strong>$795</strong>
            <span>/year</span>
          </div>
        </div>
        <p>$67/month when billed annually, with 2,500 Scale credits each month.</p>
        <ul>
          <li>Restore access to your complete Groove account</li>
          <li>Keep your existing data and customer-facing assets</li>
          <li>Get access to all 32 Scale Plus applications</li>
        </ul>
      </aside>
    </section>

    <section class="account-access__deadline" aria-labelledby="upgrade-deadline">
      <div>
        <span class="account-access__section-label">Upgrade deadline</span>
        <h2 id="upgrade-deadline">December 31, 2026 at 11:59:59 PM Eastern</h2>
        <p v-if="!hasExpired">Time remaining to upgrade and preserve account access:</p>
        <p v-else>The stated upgrade deadline has passed. View the current options available for your account.</p>
      </div>

      <div v-if="!hasExpired" class="account-access__countdown" aria-live="polite">
        <div v-for="unit in countdownUnits" :key="unit.label">
          <strong>{{ unit.value }}</strong>
          <span>{{ unit.label }}</span>
        </div>
      </div>
      <a
        v-else
        class="account-access__button account-access__button--primary"
        :href="pricingUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        View current plans
      </a>
    </section>

    <section class="account-access__details">
      <div class="account-access__warning">
        <span class="account-access__section-label">What happens after the deadline</span>
        <h2>Account data may become permanently unrecoverable.</h2>
        <p>
          Without an upgrade, your websites, domains, payment settings, products, funnels,
          checkouts, GrooveMail data and leads, videos, webinars, blogs, stores, memberships,
          courses, and other account data will no longer be available and may be permanently
          deleted and unrecoverable.
        </p>
      </div>

      <div class="account-access__utilities">
        <div>
          <h2>What you can still access</h2>
          <p>Affiliate tools and essential account services remain available.</p>
        </div>
        <nav aria-label="Available account services">
          <a :href="affiliateUrl">Affiliate dashboard</a>
          <a href="/groovesell/billing">Billing &amp; history</a>
          <a :href="supportUrl" target="_blank" rel="noopener noreferrer">Support</a>
          <scale-plus-whats-new />
        </nav>
        <p class="account-access__account-note">
          Profile, security, and logout remain available from your account menu. Data export is
          not available for restricted free accounts.
        </p>
      </div>
    </section>
  </main>
</template>

<script>
import grooveLogo from '@/assets/images/logo/logo.png';
import ScalePlusWhatsNew from '@/components/ScalePlusWhatsNew';
import { getAccountAccessPolicy } from '../../../../shared/accountAccess';

const FALLBACK_DEADLINE = '2027-01-01T04:59:59Z';

export default {
  name: 'AccountAccess',
  middleware: 'auth',
  components: {
    ScalePlusWhatsNew
  },
  data() {
    const policy = getAccountAccessPolicy() || {};

    return {
      grooveLogo,
      policy,
      now: Date.now(),
      timer: null,
      serverOffset: policy.server_time
        ? new Date(policy.server_time).getTime() - Date.now()
        : 0
    };
  },
  computed: {
    deadline() {
      return new Date(this.policy.deadline || FALLBACK_DEADLINE).getTime();
    },
    remainingMilliseconds() {
      return Math.max(0, this.deadline - (this.now + this.serverOffset));
    },
    hasExpired() {
      return this.remainingMilliseconds <= 0;
    },
    countdownUnits() {
      let remaining = Math.floor(this.remainingMilliseconds / 1000);
      const days = Math.floor(remaining / 86400);
      remaining -= days * 86400;
      const hours = Math.floor(remaining / 3600);
      remaining -= hours * 3600;
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining - minutes * 60;

      return [
        { label: 'Days', value: String(days).padStart(2, '0') },
        { label: 'Hours', value: String(hours).padStart(2, '0') },
        { label: 'Minutes', value: String(minutes).padStart(2, '0') },
        { label: 'Seconds', value: String(seconds).padStart(2, '0') }
      ];
    },
    pricingUrl() {
      return (this.policy.destinations && this.policy.destinations.pricing) || 'https://scale.gg/pricing/';
    },
    affiliateUrl() {
      return (this.policy.destinations && this.policy.destinations.affiliate) || '/groovesell/affiliates-dashboard';
    },
    supportUrl() {
      return (this.policy.destinations && this.policy.destinations.support) || 'http://support.groovedigital.com/';
    }
  },
  mounted() {
    this.timer = window.setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    window.clearInterval(this.timer);
  }
};
</script>

<style lang="scss" scoped>
.account-access {
  --ink: #171b3d;
  --muted: #606987;
  --line: #dfe3ee;
  --surface: #ffffff;
  --canvas: #f5f6fa;
  --coral: #ff5c7d;
  --green: #dceeb1;
  --lavender: #c5b0f4;

  min-height: calc(100vh - 52px);
  padding: 48px clamp(20px, 5vw, 76px) 64px;
  color: var(--ink);
  background: var(--canvas);
  font-family: inherit;
}

.account-access__intro {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.72fr);
  gap: clamp(36px, 6vw, 88px);
  align-items: center;
  max-width: 1240px;
  margin: 0 auto;
}

.account-access__eyebrow {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  color: #e73f68;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
}

.account-access__eyebrow img {
  width: 42px;
  height: 30px;
  object-fit: contain;
}

.account-access h1,
.account-access h2,
.account-access p {
  letter-spacing: 0;
}

.account-access h1 {
  max-width: 760px;
  margin: 0;
  font-size: 48px;
  line-height: 1.08;
  font-weight: 750;
}

.account-access__lead {
  max-width: 720px;
  margin: 24px 0 0;
  color: var(--muted);
  font-size: 18px;
  line-height: 1.65;
}

.account-access__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 30px;
}

.account-access__button {
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  border-radius: 6px;
  color: var(--ink);
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;
}

.account-access__button:hover,
.account-access__button:focus-visible {
  transform: translateY(-1px);
  box-shadow: 0 7px 18px rgba(23, 27, 61, 0.14);
}

.account-access__button--primary {
  background: var(--coral);
  border-color: var(--coral);
  color: #ffffff;
}

.account-access__button--affiliate {
  background: var(--lavender);
}

.account-access__reassurance {
  max-width: 760px;
  margin: 28px 0 0;
  padding-left: 18px;
  border-left: 3px solid #59b690;
  color: #46506d;
  font-size: 14px;
  line-height: 1.7;
}

.account-access__plan {
  padding: 30px;
  border: 1px solid #aac875;
  border-radius: 8px;
  background: var(--green);
  box-shadow: 12px 12px 0 #ebe6f7;
}

.account-access__plan-label,
.account-access__section-label {
  display: inline-block;
  margin-bottom: 14px;
  color: #414763;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.account-access__plan h2 {
  margin: 0 0 18px;
  font-size: 34px;
}

.account-access__prices {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.account-access__prices div {
  padding-right: 12px;
  border-right: 1px solid rgba(23, 27, 61, 0.22);
}

.account-access__prices div:last-child {
  border-right: 0;
}

.account-access__prices strong {
  display: block;
  font-size: 34px;
  line-height: 1;
}

.account-access__prices span,
.account-access__plan p,
.account-access__plan li {
  color: #343a53;
  font-size: 13px;
  line-height: 1.55;
}

.account-access__plan ul {
  margin: 20px 0 0;
  padding-left: 18px;
}

.account-access__plan li + li {
  margin-top: 8px;
}

.account-access__deadline {
  max-width: 1240px;
  margin: 54px auto 0;
  padding: 28px 0;
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
}

.account-access__deadline h2,
.account-access__details h2 {
  margin: 0;
  font-size: 22px;
  line-height: 1.3;
}

.account-access__deadline p,
.account-access__details p {
  margin: 8px 0 0;
  color: var(--muted);
  line-height: 1.6;
}

.account-access__countdown {
  display: grid;
  grid-template-columns: repeat(4, 82px);
  gap: 8px;
}

.account-access__countdown div {
  min-height: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--surface);
}

.account-access__countdown strong {
  font-size: 24px;
  font-variant-numeric: tabular-nums;
}

.account-access__countdown span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
}

.account-access__details {
  max-width: 1240px;
  margin: 40px auto 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 52px;
}

.account-access__warning {
  padding: 8px 0 8px 22px;
  border-left: 3px solid var(--coral);
}

.account-access__utilities nav {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 18px;
}

.account-access__utilities nav > a {
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  padding: 0 15px;
  border: 1px solid #cfd4e2;
  border-radius: 6px;
  background: #ffffff;
  color: var(--ink);
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
}

.account-access__account-note {
  font-size: 13px;
}

@media (max-width: 960px) {
  .account-access__intro,
  .account-access__details {
    grid-template-columns: 1fr;
  }

  .account-access__plan {
    max-width: 560px;
  }

  .account-access__deadline {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 560px) {
  .account-access {
    padding: 30px 18px 44px;
  }

  .account-access h1 {
    font-size: 34px;
  }

  .account-access__lead {
    font-size: 16px;
  }

  .account-access__actions,
  .account-access__button {
    width: 100%;
  }

  .account-access__plan {
    padding: 24px 20px;
    box-shadow: 7px 7px 0 #ebe6f7;
  }

  .account-access__countdown {
    width: 100%;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .account-access__countdown div {
    min-width: 0;
    min-height: 68px;
  }

  .account-access__countdown strong {
    font-size: 20px;
  }

  .account-access__countdown span {
    font-size: 8px;
  }
}
</style>
